import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScanInput } from '@/components/po/ScanInput'
import { ReceivingTable, receiveStateOf } from '@/components/po/ReceivingTable'
import { ProductSearchPicker } from '@/components/po/ProductSearchPicker'
import { SessionSummary } from '@/components/po/SessionSummary'
import { usePO } from '@/hooks/usePOs'
import { useReceiveScan, useReceiveAll } from '@/hooks/useReceiveScan'
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner'
import { useArchivePO, useAssignBarcode, useAddPOItem, useSyncReceived } from '@/hooks/usePOMutations'
import { KentuckyPanel } from '@/components/po/KentuckyPanel'
import { SyncOffBanner } from '@/components/shared/SyncOffBanner'
import { cn } from '@/lib/utils'
import { fmtDate, fmtTimeWithSeconds } from '@/lib/dates'
import type { components } from '@/lib/api/generated'
import type { POLineItemFormValues } from '@/lib/schemas/po'

type POItem = components['schemas']['POLineItemResponse']

// Legend for the row stripes: swatch + label + count. Not interactive — it
// explains the stripe colours and how many lines are in each state.
function LegendItem({
  swatch,
  label,
  count,
  testId,
}: {
  swatch: string
  label: string
  count: number
  testId: string
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px]" data-testid={testId}>
      <span className={cn('h-2 w-2 shrink-0', swatch)} aria-hidden />
      <span className="text-[var(--text-primary)]">{label}</span>
      <span className="tabular-nums text-[var(--mute)]">· {count}</span>
    </span>
  )
}

export default function Receiving() {
  const { poId } = useParams<{ poId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: po, isLoading } = usePO(poId ?? null)
  const scanMutation = useReceiveScan(poId ?? '')
  const receiveAllMutation = useReceiveAll(poId ?? '')
  const archiveMutation = useArchivePO()
  const assignBarcodeMutation = useAssignBarcode()
  const addItemMutation = useAddPOItem()
  const syncReceivedMutation = useSyncReceived()

  const [lastError, setLastError] = React.useState<string | null>(null)
  // Unknown barcode = amber attention; failed request = red fault.
  const [lastErrorKind, setLastErrorKind] = React.useState<'not_found' | 'failed'>(
    'not_found'
  )
  const [lastScanTime, setLastScanTime] = React.useState<string | null>(null)
  const [archiveOpen, setArchiveOpen] = React.useState(false)
  const [saveExitOpen, setSaveExitOpen] = React.useState(false)
  const [addProductOpen, setAddProductOpen] = React.useState(false)
  const [assigningId, setAssigningId] = React.useState<number | null>(null)

  // Net units received during THIS receiving session, keyed by po_item_id. The
  // item's own qty_received is the cumulative total across every session, so it
  // can't tell the user what they scanned this visit — this map can. Negative
  // corrections (the "−" button) net out here too.
  const [sessionReceipts, setSessionReceipts] = React.useState<
    Record<number, number>
  >({})
  const recordReceipt = React.useCallback((poItemId: number, qty: number) => {
    setSessionReceipts((prev) => ({
      ...prev,
      [poItemId]: (prev[poItemId] ?? 0) + qty,
    }))
  }, [])

  // Single Audio element reused for every unmatched scan. Built lazily so SSR /
  // jsdom test environments without `Audio` don't blow up on module load.
  const errorAudioRef = React.useRef<HTMLAudioElement | null>(null)
  function playScanError() {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return
    if (!errorAudioRef.current) {
      errorAudioRef.current = new Audio('/scan-error.mp3')
    }
    const audio = errorAudioRef.current
    try {
      audio.currentTime = 0
      const result = audio.play()
      // Real browsers return a promise; some test envs (jsdom) return undefined.
      // Swallow rejections so a missing autoplay policy never breaks scanning.
      if (result && typeof result.catch === 'function') {
        void result.catch(() => {})
      }
    } catch {
      /* ignore — audio is best-effort feedback, not load-bearing */
    }
  }

  const items = po?.items ?? []
  const ordered = items.reduce((s, i) => s + i.qty_ordered, 0)
  const scanned = items.reduce((s, i) => s + i.qty_received, 0)
  const delta = ordered - scanned
  const linesTotal = items.length
  const linesComplete = items.filter((i) => i.qty_received >= i.qty_ordered).length
  const linesOpen = linesTotal - linesComplete
  const linesPartial = items.filter(
    (i) => i.qty_received > 0 && i.qty_received < i.qty_ordered
  ).length
  // Same classifier as the table's row stripes, so the legend always agrees.
  const linesToScan = items.filter((i) => receiveStateOf(i) === 'todo').length
  const progressPct =
    ordered > 0 ? Math.min(100, Math.round((scanned / ordered) * 100)) : 0

  const handleScan = React.useCallback(
    (barcode: string) => {
      scanMutation.mutate(
        { barcode },
        {
          onSuccess: (result) => {
            if (!result.matched) {
              setLastErrorKind('not_found')
              setLastError(`Barcode "${barcode}" not found in this PO`)
              playScanError()
            } else {
              setLastError(null)
              setLastScanTime(fmtTimeWithSeconds(new Date()))
              if (result.po_item_id != null) {
                recordReceipt(result.po_item_id, result.qty)
              }
            }
          },
          onError: () => {
            setLastErrorKind('failed')
            setLastError(`Scan failed for "${barcode}"`)
            playScanError()
          },
        }
      )
    },
    [scanMutation, recordReceipt]
  )

  // Window-level scanner: catches scans even when focus is off the input.
  useBarcodeScanner(handleScan)

  const handleManualReceive = React.useCallback(
    (item: POItem, qty: number) => {
      if (!item.barcode && !item.variant_id) {
        toast.error('Item has no barcode or variant id — cannot receive')
        return
      }
      scanMutation.mutate(
        { barcode: item.barcode || '', variant_id: item.variant_id, qty },
        {
          onSuccess: (result) => {
            if (!result.matched) {
              toast.error(`Could not match ${item.product_title}`)
            } else {
              setLastError(null)
              setLastScanTime(fmtTimeWithSeconds(new Date()))
              if (result.po_item_id != null) {
                recordReceipt(result.po_item_id, result.qty)
              }
            }
          },
        }
      )
    },
    [scanMutation, recordReceipt]
  )

  const handleAssignBarcode = React.useCallback(
    (item: POItem, barcode: string) => {
      if (!poId) return
      setAssigningId(item.po_item_id)
      assignBarcodeMutation.mutate(
        { po_id: poId, po_item_id: item.po_item_id, barcode },
        {
          onSuccess: () => toast.success(`Barcode assigned to ${item.product_title}`),
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to assign barcode'),
          onSettled: () => setAssigningId(null),
        }
      )
    },
    [poId, assignBarcodeMutation]
  )

  const handleAddProduct = React.useCallback(
    (picked: POLineItemFormValues) => {
      if (!poId) return
      addItemMutation.mutate(
        {
          po_id: poId,
          item: {
            variant_id: picked.variant_id,
            product_id: picked.product_id,
            product_title: picked.product_title,
            variant_title: picked.variant_title,
            sku: picked.sku,
            barcode: picked.barcode,
            vendor: picked.vendor,
            retail_price: picked.retail_price,
            cost_base: picked.cost_base,
            qty_ordered: picked.qty_ordered,
            notes: picked.notes,
          },
        },
        {
          // Keep the modal open so the user can add several products in a row;
          // they close it themselves when done.
          onSuccess: () => toast.success(`Added ${picked.product_title} to this PO`),
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to add product'),
        }
      )
    },
    [poId, addItemMutation]
  )

  const handleReceiveAll = React.useCallback(() => {
    // Only items still short AND matchable (have a barcode or variant_id).
    const remainingItems = items.filter(
      (i) => i.qty_ordered - i.qty_received > 0 && (i.barcode || i.variant_id)
    )
    if (remainingItems.length === 0) return
    const totalUnits = remainingItems.reduce(
      (s, i) => s + (i.qty_ordered - i.qty_received),
      0
    )
    if (
      !window.confirm(
        `Receive all remaining? Marks ${totalUnits} unit${totalUnits === 1 ? '' : 's'} ` +
          `across ${remainingItems.length} item${remainingItems.length === 1 ? '' : 's'} as received.`
      )
    ) {
      return
    }
    const scans = remainingItems.map((i) => ({
      barcode: i.barcode || '',
      variant_id: i.variant_id,
      qty: i.qty_ordered - i.qty_received,
    }))
    receiveAllMutation.mutate(scans, {
      onSuccess: (data) => {
        data.results.forEach((r) => {
          if (r.matched && r.po_item_id != null) recordReceipt(r.po_item_id, r.qty)
        })
        setLastError(null)
        setLastScanTime(fmtTimeWithSeconds(new Date()))
        toast.success(
          `Received all remaining · ${totalUnits} unit${totalUnits === 1 ? '' : 's'}`
        )
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Receive all failed'),
    })
  }, [items, receiveAllMutation, recordReceipt])

  function handleArchiveConfirm() {
    if (!poId) return
    archiveMutation.mutate(poId, {
      onSuccess: () => {
        toast.success('PO archived')
        navigate('/purchase-orders')
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Archive failed')
        setArchiveOpen(false)
      },
    })
  }

  // Save & exit now also flushes received-so-far to Shopify (AVAILABLE +=
  // new receipts, INCOMING cleared) so arriving stock is sellable before the
  // PO is fully archived. The receipts are already saved locally per-scan, so
  // this is best-effort: whatever the sync does, we still navigate away.
  function handleSaveExit() {
    if (!poId) {
      navigate('/purchase-orders')
      return
    }
    const toastId = toast.loading('Saving · syncing received units to Shopify…')
    syncReceivedMutation.mutate(poId, {
      onSuccess: (resp) => {
        toast.dismiss(toastId)
        // `sync` is an untyped dict on the wire (po_sync.* result shape:
        // {skipped, synced[], failed[]}); narrow it locally so the toast logic
        // typechecks under the build's `tsc -b`.
        const sync = resp?.sync as
          | { skipped?: string | null; synced?: unknown[]; failed?: unknown[] }
          | null
          | undefined
        const failed = sync?.failed?.length ?? 0
        // Shopify push genuinely didn't run (sync turned off / offline / no
        // creds). "no_eligible_items" means there was simply nothing new to
        // push, which still counts as in-sync — don't flag that.
        const syncOff =
          sync?.skipped === 'sync_disabled' ||
          sync?.skipped === 'offline_mode' ||
          sync?.skipped === 'missing_credentials'
        if (failed > 0) {
          toast.warning(
            `Progress saved · ${scanned} of ${ordered} units received. ${failed} item${failed === 1 ? '' : 's'} failed to sync to Shopify — will retry at archive.`
          )
        } else if (syncOff) {
          toast.warning(
            `Progress saved · ${scanned} of ${ordered} units received. Shopify sync is off, so received units weren't sent to Shopify. They'll go across when sync is back on and you Save & exit, Finish & archive, or use Retry Sync.`
          )
        } else {
          toast.success(
            `Progress saved · ${scanned} of ${ordered} units received and synced to Shopify. Return any time from the PO list.`
          )
        }
        queryClient.invalidateQueries({ queryKey: ['pos'] })
        navigate('/purchase-orders')
      },
      onError: (err) => {
        toast.dismiss(toastId)
        toast.error(
          `Progress saved locally, but Shopify sync failed: ${err instanceof Error ? err.message : 'unknown error'}. It will reconcile when you Finish & archive.`
        )
        queryClient.invalidateQueries({ queryKey: ['pos'] })
        navigate('/purchase-orders')
      },
    })
  }

  if (isLoading || !po) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-[var(--mute)]">
        Loading…
      </div>
    )
  }

  const poLabel = po.po_number ? `#${po.po_number}` : po.po_id
  const supplierOrVendor = (po.supplier ?? '').trim() || po.vendor
  const orderedDate = fmtDate(po.ordered_date, '—')

  return (
    <div className="flex min-h-screen flex-col brand-theme bg-[var(--surface-page)] text-[var(--text-primary)]">
      {/* No TopTabs on this page, so the sync-off banner goes here. */}
      <SyncOffBanner />
      {/* Page header — V1 spec */}
      <div className="flex items-center gap-3 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[14px]">
        <Button
          variant="ghost"
          size="sm"
          className="h-[22px] px-2 text-[11px] text-[var(--ink-2)] hover:text-[var(--text-primary)]"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['pos'] })
            navigate('/purchase-orders')
          }}
          data-testid="back-btn"
        >
          ← Back
        </Button>
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold leading-tight tracking-[-0.01em] text-[var(--text-primary)]">
            Receive · {poLabel}
          </h1>
          <div className="text-[12px] text-[var(--mute)]">
            {supplierOrVendor}
            {po.location_to_receive && ` → ${po.location_to_receive}`}
            {po.ordered_date && ` · ordered ${orderedDate}`}
          </div>
        </div>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          className="h-[22px] border-[var(--line-warm)] px-2 text-[11px] text-[var(--ink-2)]"
          onClick={() => setAddProductOpen(true)}
          data-testid="add-product-btn"
        >
          Add product
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-[22px] border-[var(--line-warm)] px-2 text-[11px] text-[var(--ink-2)]"
          onClick={() => toast('Report damage — not yet implemented')}
        >
          Report damage
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-[22px] border-[var(--line-warm)] px-2 text-[11px] text-[var(--ink-2)]"
          onClick={handleReceiveAll}
          disabled={linesOpen === 0 || receiveAllMutation.isPending}
          title={
            linesOpen === 0
              ? 'All items already received'
              : 'Receive all remaining units in one step'
          }
          data-testid="receive-all-btn"
        >
          {receiveAllMutation.isPending ? 'Receiving…' : 'Receive all'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-[22px] border-[var(--text-primary)] px-2 text-[11px] font-semibold text-[var(--text-primary)]"
          onClick={() => setSaveExitOpen(true)}
          disabled={syncReceivedMutation.isPending}
          data-testid="save-exit-btn"
        >
          {syncReceivedMutation.isPending ? 'Saving…' : 'Save & exit'}
        </Button>
        <Button
          size="sm"
          className="h-[22px] px-2 text-[11px] font-semibold"
          onClick={() => setArchiveOpen(true)}
          disabled={archiveMutation.isPending}
          data-testid="mark-complete-btn"
        >
          Finish &amp; archive…
        </Button>
      </div>

      {/* Scan + stats row */}
      <div className="flex items-start gap-6 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-[18px] py-[16px]">
        {/* Kentucky Horsewear transit info — left of the scanbar (only renders
            for a transmitted Kentucky PO; otherwise the scanbar fills the row) */}
        <KentuckyPanel po={po} className="w-[360px]" />
        <div className="flex-1">
          <ScanInput
            onScan={handleScan}
            isPending={scanMutation.isPending}
            lastError={lastError}
            lastErrorKind={lastErrorKind}
            onErrorDismiss={() => setLastError(null)}
          />
        </div>
        <div className="w-[360px]">
          <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
            Received
          </div>
          <div
            className="flex items-baseline gap-1.5"
            data-testid="progress-summary"
          >
            <div className="text-[28px] font-semibold tabular-nums leading-none">
              {scanned}
            </div>
            <div className="text-[12px] text-[var(--mute)]">/ {ordered} units</div>
          </div>
          <div
            className="relative mt-1.5 h-[6px] w-full overflow-hidden border border-[var(--line-warm)] bg-[var(--meter-track)]"
            aria-label={`${progressPct}% of units received`}
          >
            <div
              className={cn(
                'absolute inset-y-0 left-0 transition-[width]',
                ordered > 0 && scanned >= ordered ? 'bg-[var(--meter-good)]' : 'bg-[var(--brand)]'
              )}
              style={{ width: progressPct + '%' }}
              data-testid="progress-bar"
            />
          </div>
          <div className="mt-2 flex gap-3.5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
                SKUs done
              </div>
              <div className="text-[12px] font-semibold tabular-nums text-[var(--text-primary)]">
                {linesComplete}/{linesTotal}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--mute)]">
                Last scan
              </div>
              <div className="text-[12px] font-semibold tabular-nums text-[var(--text-primary)]">
                {lastScanTime || '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stripe legend */}
      <div className="flex items-center gap-4 border-b border-[var(--line-warm)] bg-[var(--surface-card)] px-4 py-2">
        <LegendItem
          swatch="bg-[var(--receive-todo)]"
          label="To scan"
          count={linesToScan}
          testId="legend-todo"
        />
        <LegendItem
          swatch="bg-[var(--meter-attention)]"
          label="Partial"
          count={linesPartial}
          testId="legend-partial"
        />
        <LegendItem
          swatch="bg-[var(--meter-good)]"
          label="Received"
          count={linesComplete}
          testId="legend-received"
        />
        <div className="flex-1" />
        <div className="text-[10px] text-[var(--mute)]">
          ⏎ scan · Esc clear input · Save &amp; exit syncs received units &amp; pauses · Finish &amp; archive when done
        </div>
      </div>

      {/* Items table */}
      {items.length > 0 && (
        <ReceivingTable
          items={items}
          onManualReceive={handleManualReceive}
          onAssignBarcode={handleAssignBarcode}
          assignBarcodePendingId={assigningId}
        />
      )}

      {/* Save & exit — session recap before pausing */}
      <Dialog open={saveExitOpen} onOpenChange={setSaveExitOpen}>
        <DialogContent
          className="brand-theme max-h-[85vh] overflow-y-auto border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[640px]"
          data-testid="save-exit-dialog"
        >
          <DialogHeader>
            <DialogTitle>Save &amp; exit</DialogTitle>
            <DialogDescription data-testid="save-exit-summary">
              {scanned} of {ordered} units received
              {delta > 0 ? ` · ${delta} still open` : ''}. This saves your progress
              and syncs newly received units to Shopify (when sync is on) — resume
              any time from the PO list.
            </DialogDescription>
          </DialogHeader>
          <SessionSummary items={items} sessionReceipts={sessionReceipts} />
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSaveExitOpen(false)}
              disabled={syncReceivedMutation.isPending}
            >
              Keep receiving
            </Button>
            <Button
              size="sm"
              onClick={handleSaveExit}
              disabled={syncReceivedMutation.isPending}
              data-testid="save-exit-confirm-btn"
            >
              {syncReceivedMutation.isPending ? 'Saving…' : 'Save & exit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent
          showCloseButton={false}
          className="brand-theme max-h-[85vh] overflow-y-auto border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[640px]"
          data-testid="archive-dialog"
        >
          <DialogHeader>
            <DialogTitle>Finish &amp; archive this PO?</DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-col gap-1 text-sm">
                <p data-testid="archive-summary">
                  {scanned} of {ordered} units received
                  {delta > 0 && (
                    <span className="text-[var(--status-awaiting-fg)]">
                      {` (${delta} short)`}
                    </span>
                  )}
                </p>
                <p className="text-[var(--mute)]">
                  Archiving finalizes this PO in Shopify (any receipts already
                  synced on Save &amp; exit are reconciled, not double-counted):
                </p>
                <ul className="ml-4 list-disc text-[var(--mute)]">
                  <li>
                    Ensure {scanned} unit{scanned === 1 ? '' : 's'} are in AVAILABLE
                    at {po.location_to_receive ?? 'your location'} in Shopify
                  </li>
                  <li>
                    {delta > 0
                      ? `Clear ${delta} residual unit${delta === 1 ? '' : 's'} from INCOMING in Shopify (never arrived; removed from forecasting)`
                      : 'Clear this PO from INCOMING in Shopify'}
                  </li>
                  {delta > 0 && (
                    <li>
                      Send {delta} unreceived unit{delta === 1 ? '' : 's'} to the
                      backorder / OOS list (tracked per brand with today's date)
                    </li>
                  )}
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <SessionSummary items={items} sessionReceipts={sessionReceipts} />
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setArchiveOpen(false)}
              disabled={archiveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleArchiveConfirm}
              disabled={archiveMutation.isPending}
              data-testid="archive-confirm-btn"
            >
              {archiveMutation.isPending ? 'Archiving…' : 'Archive PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add product to PO */}
      <Dialog open={addProductOpen} onOpenChange={setAddProductOpen}>
        <DialogContent
          className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
          data-testid="add-product-dialog"
        >
          <DialogHeader>
            <DialogTitle>Add a product to this PO</DialogTitle>
            <DialogDescription>
              Search {po.vendor || 'this vendor'}’s catalog and pick a product to
              add. It’s added to the PO immediately so you can scan it in — Shopify
              is updated when you Save &amp; exit or Finish &amp; archive.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <ProductSearchPicker
              onPick={handleAddProduct}
              disabled={addItemMutation.isPending}
              supplier={po.supplier || null}
              vendor={po.vendor || null}
              autoPickOnBarcodeMatch
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddProductOpen(false)}
              disabled={addItemMutation.isPending}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
